// src/components/TradeSettingsCard.js
import React, { useEffect, useState } from "react";
import { apiFetch } from "../apiClient";

export default function TradeSettingsCard({ userId, mode }) {
    const [settings, setSettings] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // NEW: local account size for recommendations
    const [accountSize, setAccountSize] = useState("");
    const [recError, setRecError] = useState("");

    const daily = Number(settings?.daily_risk_cap_usd || 0);
    const perTrade = Number(settings?.per_trade_risk_usd || 0);
    const maxPositions = Number(settings?.max_positions || 0);

    const combinedRisk = perTrade * maxPositions;
    const effectiveMaxRisk = Math.max(0, Math.min(daily || combinedRisk, combinedRisk || daily));

    const acct = Number(accountSize || 0);
    const riskPct = acct > 0 && effectiveMaxRisk > 0
        ? Math.min(100, (effectiveMaxRisk / acct) * 100)
        : 0;


    // Load current settings
    useEffect(() => {
        if (!userId) return;

        const load = async () => {
            try {
                const data = await apiFetch(
                    `/api/trade/settings?user_id=${encodeURIComponent(userId)}`
                );
                // Backend now returns the settings as a flat object,
                // but keep compatibility if "settings" wrapper exists.
                const s = data.settings || data || {};
                setSettings(s);
            } catch (err) {
                console.error("Failed to load trade settings:", err);
                setError("Failed to load trade settings");
            }
        };


        load();
    }, [userId]);

    const handleChange = (field, value) => {
        setSettings((prev) => ({
            ...(prev || {}),
            [field]: value,
        }));
    };

    const applyRecommended = () => {
        setRecError("");

        const raw = (accountSize || "").trim();
        const parsed = Number(raw);
        if (!parsed || parsed <= 0) {
            setRecError("Enter a positive account size first.");
            return;
        }

        const equity = parsed;

        // Simple rules of thumb
        const daily = Math.round(equity * 0.02);     // 2% per day
        const perTrade = Math.round(equity * 0.01);  // 1% per trade
        const maxPos = Math.round(equity * 0.10);    // 10% per position

        let maxPositions = 1;
        if (perTrade > 0) {
            maxPositions = Math.max(1, Math.floor(daily / perTrade));
        }

        setSettings((prev) => ({
            ...(prev || {}),
            daily_risk_cap_usd: daily,
            per_trade_risk_usd: perTrade,
            max_position_usd: maxPos,
            max_positions: maxPositions,
        }));
    };


    const saveSettings = async () => {
        if (!userId || !settings) return;
        setSaving(true);
        setError(null);

        try {
            await apiFetch("/api/trade/settings", {
                method: "POST",
                body: JSON.stringify({
                    user_id: userId,
                    daily_risk_cap_usd: Number(settings.daily_risk_cap_usd) || 0,
                    max_position_usd: Number(settings.max_position_usd) || 0,
                    max_positions: Number(settings.max_positions) || 0,
                    per_trade_risk_usd: Number(settings.per_trade_risk_usd) || 0,
                    option_soft_stop_pct: Number(settings.option_soft_stop_pct) || 0,
                    trading_window_start: settings.trading_window_start || "09:35",
                    trading_window_end: settings.trading_window_end || "15:50",
                    auto_trade_enabled: settings.auto_trade_enabled ? 1 : 0,
                    auto_exit_enabled: settings.auto_exit_enabled ? 1 : 0,
                }),
            });

            // re-load from backend to be sure
            const refreshed = await apiFetch(
                `/api/trade/settings?user_id=${encodeURIComponent(userId)}`
            );
            const rs = refreshed.settings || refreshed || {};
            setSettings(rs);

        } catch (err) {
            console.error("Failed to save trade settings:", err);
            setError("Failed to save settings");
        } finally {
            setSaving(false);
        }
    };

    if (!userId) {
        return (
            <div className="card mt-4">
                <div className="card-body text-sm text-gray-300">
                    Sign in to configure your trade settings.
                </div>
            </div>
        );
    }

    if (!settings) {
        return (
            <div className="card mt-4">
                <div className="card-body text-sm text-gray-300">Loading settings�</div>
            </div>
        );
    }

    return (
        <div className="card mt-4">
            <div className="card-header flex items-center justify-between">
                <div>
                    <h3 className="card-title text-base text-white">Risk &amp; Automation</h3>
                    <p className="text-xs text-gray-400">
                        Currently editing settings for{" "}
                        <span className="font-semibold">
                            {mode === "live" ? "LIVE" : "PAPER"}
                        </span>{" "}
                        trading. Auto Trade and Auto Exits are controlled by the toggles at
                        the top of the Paper &amp; Live Trading page.
                    </p>
                </div>
                <button
                    onClick={saveSettings}
                    disabled={saving}
                    className="px-3 py-1 rounded-md bg-emerald-600 hover:bg-emerald-500 text-sm font-semibold text-white disabled:opacity-60"
                >
                    {saving ? "Saving�" : "Save"}
                </button>
            </div>

            {/* Recommended values helper */}
            <div className="mt-3 flex flex-col gap-2 rounded-lg bg-zinc-900/70 border border-zinc-700 p-3">
                <div className="flex flex-wrap items-center gap-2 justify-between">
                    <div className="text-xs text-zinc-300">
                        <span className="font-semibold">Recommended risk profile</span>
                        <span className="ml-1 text-[11px] text-zinc-400">
                            (based on your account size)
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            className="w-32 px-2 py-1 text-xs rounded bg-zinc-950 border border-zinc-700 text-zinc-100"
                            placeholder="Account size"
                            value={accountSize}
                            onChange={(e) => setAccountSize(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={applyRecommended}
                            className="px-3 py-1 text-xs rounded bg-emerald-600 hover:bg-emerald-500 text-white"
                        >
                            Apply recommended
                        </button>
                    </div>
                </div>
                {recError && (
                    <div className="text-[11px] text-red-400">{recError}</div>
                )}
                <div className="text-[11px] text-zinc-400">
                    Uses common rules of thumb: ~2% of account per day, ~1% per trade,
                    and max position ~10% of account.
                </div>
            </div>

            <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-200">
                <div className="space-y-2">
                    <label className="flex flex-col gap-1">
                        <span>Daily risk cap (USD)</span>
                        <p className="text-[11px] text-gray-400">
                            The maximum amount you are willing to lose in a single day.
                            Auto-trading stops for the rest of the day if this limit is hit.
                        </p>
                        <input
                            type="number"
                            className="input bg-zinc-900 text-zinc-100 placeholder-zinc-500 border border-zinc-700"
                            value={settings.daily_risk_cap_usd ?? ""}
                            onChange={(e) =>
                                handleChange("daily_risk_cap_usd", e.target.value)
                            }
                        />
                    </label>

                    <label className="flex flex-col gap-1">
                        <span>Max position size (USD)</span>
                        <p className="text-[11px] text-gray-400">
                            The maximum dollar size of any single trade.
                            The AI will not open positions larger than this amount.
                        </p>
                        <input
                            type="number"
                            className="input bg-zinc-900 text-zinc-100 placeholder-zinc-500 border border-zinc-700"
                            value={settings.max_position_usd ?? ""}
                            onChange={(e) =>
                                handleChange("max_position_usd", e.target.value)
                            }
                        />
                    </label>

                    <label className="flex flex-col gap-1">
                        <span>Max open positions</span>
                        <p className="text-[11px] text-gray-400">
                            The maximum number of trades allowed at the same time.
                            If this limit is reached, the AI will not open new trades.
                        </p>
                        <input
                            type="number"
                            className="input bg-zinc-900 text-zinc-100 placeholder-zinc-500 border border-zinc-700"
                            value={settings.max_positions ?? ""}
                            onChange={(e) => handleChange("max_positions", e.target.value)}
                        />
                    </label>

                    <label className="flex flex-col gap-1">
                        <span>Per-trade risk (USD)</span>
                        <p className="text-[11px] text-gray-400">
                            How much you�re willing to risk on each individual trade.
                            The AI sizes positions so your loss is no more than this amount
                            if the stop-loss is hit.
                        </p>
                        <input
                            type="number"
                            className="input bg-zinc-900 text-zinc-100 placeholder-zinc-500 border border-zinc-700"
                            value={settings.per_trade_risk_usd ?? ""}
                            onChange={(e) =>
                                handleChange("per_trade_risk_usd", e.target.value)
                            }
                        />
                    </label>
                </div>

                    <label className="flex flex-col gap-1">
                        <span>Option max loss (%)</span>
                        <p className="text-[11px] text-gray-400">
                            Optional safety net for options. Example: 40 means close any option
                            at -40% or worse. Set to 0 to let the AI manage exits automatically.
                        </p>
                        <input
                            type="number"
                        className="input bg-zinc-900 text-zinc-100 placeholder-zinc-500 border border-zinc-700"
                            value={settings.option_soft_stop_pct ?? ""}
                            onChange={(e) =>
                                handleChange("option_soft_stop_pct", e.target.value)
                            }
                            placeholder="0 = AI controlled"
                        />
                    </label>

                <div className="space-y-3">
                    <label className="flex flex-col gap-1">
                        <span>Trading window start (HH:MM)</span>
                        <input
                            type="time"
                            className="input bg-zinc-900 text-zinc-100 placeholder-zinc-500 border border-zinc-700"
                            value={settings.trading_window_start ?? "09:35"}
                            onChange={(e) =>
                                handleChange("trading_window_start", e.target.value)
                            }
                        />
                    </label>

                    <label className="flex flex-col gap-1">
                        <span>Trading window end (HH:MM)</span>
                        <input
                            type="time"
                            className="input bg-zinc-900 text-zinc-100 placeholder-zinc-500 border border-zinc-700"
                            value={settings.trading_window_end ?? "15:50"}
                            onChange={(e) =>
                                handleChange("trading_window_end", e.target.value)
                            }
                        />
                    </label>
                    {/* Simulated max risk preview */}
                    <div className="mt-4 rounded-xl border border-zinc-700 bg-zinc-950 px-3 py-3 space-y-2">
                        <div className="flex items-center justify-between text-xs text-zinc-200">
                            <span className="font-semibold">Simulated max daily risk</span>
                            <span className="text-zinc-300">
                                {effectiveMaxRisk > 0 ? (
                                    <>
                                        ${effectiveMaxRisk.toLocaleString()}
                                        {acct > 0 && riskPct > 0 && (
                                            <> (~{riskPct.toFixed(1)}% of account)</>
                                        )}
                                    </>
                                ) : (
                                    "Set your risk values above"
                                )}
                            </span>
                        </div>

                        <div className="h-2 w-full rounded-full bg-zinc-800 overflow-hidden">
                            <div
                                className="h-2 bg-emerald-500 transition-all"
                                style={{
                                    width:
                                        acct > 0 && riskPct > 0
                                            ? `${riskPct}%`
                                            : effectiveMaxRisk > 0
                                                ? "50%"
                                                : "0%",
                                }}
                            />
                        </div>

                        <div className="text-[11px] text-zinc-400">
                            This compares your daily risk cap with your per-trade risk and max
                            positions. The bar shows how much of your account could be at risk in
                            a worst-case day if all trades hit their stops.
                        </div>
                    </div>

                    <div className="flex items-center justify-between mt-2">
                        <span>AI Auto-Trade status</span>
                        <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${settings.auto_trade_enabled
                                    ? "bg-emerald-500 text-black"
                                    : "bg-gray-700 text-gray-300"
                                }`}
                        >
                            {settings.auto_trade_enabled ? "Enabled" : "Disabled"}
                        </span>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">
                        Toggle Auto Trade from the Auto Trade card at the top of the
                        Paper &amp; Live Trading page.
                    </p>

                    <div className="flex items-center justify-between mt-3">
                        <span>AI Auto-Exit status</span>
                        <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${settings.auto_exit_enabled
                                    ? "bg-emerald-500 text-black"
                                    : "bg-gray-700 text-gray-300"
                                }`}
                        >
                            {settings.auto_exit_enabled ? "Enabled" : "Disabled"}
                        </span>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1">
                        Toggle Auto Exits from the Auto Exits card at the top of the
                        Paper &amp; Live Trading page.
                    </p>


                    {error && (
                        <p className="mt-2 text-xs text-red-400">
                            {error} � try again or refresh.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
